import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import DataTable, {
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableHeader,
} from '@amzn/meridian/data-table';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Box from '@amzn/meridian/box';
import Loader from '@amzn/meridian/loader';
import Checkbox from '@amzn/meridian/checkbox';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import Tooltip from '@amzn/meridian/tooltip';
import Icon from '@amzn/meridian/icon';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  applyDryRunShiftPlan,
  generatePlanThunk,
  setOutputAdjustment,
  removeOutputAdjustment,
  clearOutputAdjustments,
  dismissConfirmation,
} from '../../store/slices/checkoutSlice';
import { showError } from '../../store/slices/errorSlice';
import { ROUTES } from '../../utils/constants';
import { RCC_SHIFT_PLAN_CHECKOUT_TAB } from '../../utils/metric-utils';
import { ProcessPathFunctionType, DryRunProcessPathEntry, OVERRIDE_REASON_OPTIONS } from './model';
import SummaryStatsRow from './components/SummaryStatsRow';
import TuningSlider from './components/TuningSlider';
import ConfirmPlanGeneration from './components/ConfirmPlanGeneration';
import './index.scss';

const Checkout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Reset stale confirmation state on mount (prevents landing on confirmation page)
  useEffect(() => {
    dispatch(dismissConfirmation());
  }, [dispatch]);

  const planId = useAppSelector((state) => state.shiftPlan.planId);

  const {
    dryRunResponse,
    dryRunLoading,
    outputAdjustments,
    appliedDryRunLoading,
    showConfirmation,
    generatePlanLoading,
  } = useAppSelector((state) => state.checkout);

  // Sort process paths: DIRECT first, then by headcount descending
  const sortedProcessPaths = useMemo(() => {
    if (!dryRunResponse?.processPath) return [];
    return [...dryRunResponse.processPath].sort((a, b) => {
      if (a.type === b.type) return b.headcount - a.headcount;
      return a.type === ProcessPathFunctionType.DIRECT ? -1 : 1;
    });
  }, [dryRunResponse]);

  // Compute derived stats
  const stats = useMemo(() => {
    if (!dryRunResponse) {
      return { includedPathCount: 0, excludedPathCount: 0, tunedHeadcount: 0, totalHours: 0, directHours: 0, indirectHours: 0 };
    }

    const excludedPaths = outputAdjustments
      .filter((adj) => adj.disabled)
      .map((adj) => adj.functionName);

    const included = dryRunResponse.processPath.filter(
      (pp) => !excludedPaths.includes(pp.functionName)
    );

    const directHours = parseFloat(included
      .filter((pp) => pp.type === ProcessPathFunctionType.DIRECT)
      .reduce((sum, pp) => {
        const pct = outputAdjustments.find((a) => a.functionName === pp.functionName && !a.disabled)?.overridePercentage ?? 100;
        return sum + (pp.hours || 0) * pct / 100;
      }, 0).toFixed(2));
    const indirectHours = parseFloat(included
      .filter((pp) => pp.type === ProcessPathFunctionType.INDIRECT)
      .reduce((sum, pp) => {
        const pct = outputAdjustments.find((a) => a.functionName === pp.functionName && !a.disabled)?.overridePercentage ?? 100;
        return sum + (pp.hours || 0) * pct / 100;
      }, 0).toFixed(2));

    // Compute tuned headcount (sum of adjusted HC for included paths)
    const tunedHeadcount = included.reduce((sum, pp) => {
      const pct = outputAdjustments.find((a) => a.functionName === pp.functionName && !a.disabled)?.overridePercentage ?? 100;
      const adjusted = Math.round(pp.headcount * pct / 100);
      const capped = pp.maxStaffing ? Math.min(adjusted, pp.maxStaffing) : adjusted;
      return sum + capped;
    }, 0);

    return {
      includedPathCount: included.length,
      excludedPathCount: excludedPaths.length,
      tunedHeadcount,
      totalHours: parseFloat((directHours + indirectHours).toFixed(2)),
      directHours,
      indirectHours,
    };
  }, [dryRunResponse, outputAdjustments]);

  // Check if a path is excluded
  const isPathExcluded = useCallback((functionName: string) => {
    return outputAdjustments.some((adj) => adj.functionName === functionName && adj.disabled);
  }, [outputAdjustments]);

  // Get current override percentage for a path (100 = no change)
  const getOverridePercentage = useCallback((functionName: string) => {
    const adj = outputAdjustments.find((a) => a.functionName === functionName);
    if (!adj || adj.disabled) return 100;
    return adj.overridePercentage;
  }, [outputAdjustments]);

  // Get slider value — same as overridePercentage (0-200 scale, 100 = no change)
  const getSliderValue = useCallback((functionName: string) => {
    return getOverridePercentage(functionName);
  }, [getOverridePercentage]);

  // Local state to track "Other" free-text per path
  const [otherReasonText, setOtherReasonText] = useState<Record<string, string>>({});

  // Track whether validation errors should be shown (attempted to proceed)
  const [showReasonErrors, setShowReasonErrors] = useState(false);

  // Get the selected reason for a path (from outputAdjustments)
  const getSelectedReason = useCallback((functionName: string): string => {
    const adj = outputAdjustments.find((a) => a.functionName === functionName);
    if (!adj) return '';
    return adj.overrideReason || '';
  }, [outputAdjustments]);

  // Check if a path has been modified (has a non-zero override percentage)
  const isPathModified = useCallback((functionName: string): boolean => {
    const adj = outputAdjustments.find((a) => a.functionName === functionName);
    return !!adj && !adj.disabled && adj.overridePercentage !== 100;
  }, [outputAdjustments]);

  // Check if the selected reason is "Other" for a path
  // Options that require a free text input
  const FREE_TEXT_REASONS = ['Other', 'Manager discretion'] as const;

  // Check if the selected reason requires free text input
  const isFreeTextReason = useCallback((functionName: string): boolean => {
    const reason = getSelectedReason(functionName);
    if (FREE_TEXT_REASONS.includes(reason as any)) return true;
    // Also true if the stored reason is custom text (not in the standard options list)
    return reason !== '' && !OVERRIDE_REASON_OPTIONS.includes(reason as any);
  }, [getSelectedReason]);

  // Get the dropdown value (the category, not the free text)
  const getDropdownValue = useCallback((functionName: string): string => {
    const reason = getSelectedReason(functionName);
    if (FREE_TEXT_REASONS.includes(reason as any)) return reason;
    // If the reason is custom text, it was entered under "Other" or "Manager discretion"
    if (reason !== '' && !OVERRIDE_REASON_OPTIONS.includes(reason as any)) return 'Other';
    return reason;
  }, [getSelectedReason]);

  // Handle slider change
  const handleSliderChange = useCallback((entry: DryRunProcessPathEntry, sliderValue: number) => {
    if (sliderValue === 100) {
      dispatch(removeOutputAdjustment(entry.functionName));
    } else {
      const existingAdj = outputAdjustments.find((a) => a.functionName === entry.functionName);
      dispatch(setOutputAdjustment({
        functionId: entry.functionId,
        functionName: entry.functionName,
        overridePercentage: sliderValue,
        overrideReason: existingAdj?.overrideReason,
      }));
    }
  }, [dispatch, outputAdjustments]);

  // Handle override reason dropdown change
  const handleReasonChange = useCallback((entry: DryRunProcessPathEntry, reason: string) => {
    const existingAdj = outputAdjustments.find((a) => a.functionName === entry.functionName);
    if (!existingAdj) return;

    if (reason === 'Other' || reason === 'Manager discretion') {
      // Store the category name — user must type custom text to replace it
      dispatch(setOutputAdjustment({
        ...existingAdj,
        overrideReason: reason,
      }));
    } else {
      dispatch(setOutputAdjustment({
        ...existingAdj,
        overrideReason: reason,
      }));
      // Clear any free text for this path
      setOtherReasonText((prev) => {
        const updated = { ...prev };
        delete updated[entry.functionName];
        return updated;
      });
    }
  }, [dispatch, outputAdjustments]);

  // Handle "Other" free text input commit
  const handleOtherReasonCommit = useCallback((entry: DryRunProcessPathEntry, text: string) => {
    const existingAdj = outputAdjustments.find((a) => a.functionName === entry.functionName);
    if (!existingAdj) return;

    const fallback = getDropdownValue(entry.functionName);
    dispatch(setOutputAdjustment({
      ...existingAdj,
      overrideReason: text || fallback,
    }));
  }, [dispatch, outputAdjustments, getDropdownValue]);

  // Handle exclude/include toggle
  const handleExcludeToggle = useCallback((entry: DryRunProcessPathEntry) => {
    const currentlyExcluded = isPathExcluded(entry.functionName);
    if (currentlyExcluded) {
      // Re-include: remove the disabled adjustment
      dispatch(removeOutputAdjustment(entry.functionName));
    } else {
      // Exclude: set disabled flag
      dispatch(setOutputAdjustment({
        functionId: entry.functionId,
        functionName: entry.functionName,
        overridePercentage: 0,
        disabled: true,
      }));
    }
  }, [dispatch, isPathExcluded]);

  // Handle clear all overrides
  const handleClearOverrides = useCallback(() => {
    dispatch(clearOutputAdjustments());
  }, [dispatch]);

  // Handle apply changes — validate reasons then call dry run with adjustments
  const handleApplyChanges = useCallback(async () => {
    // Check if all modified/excluded paths have an override reason
    const missingReason = outputAdjustments.some(
      (adj) => (adj.disabled || adj.overridePercentage !== 100) && (!adj.overrideReason || adj.overrideReason === 'Other' || adj.overrideReason === 'Manager discretion')
    );

    if (missingReason) {
      setShowReasonErrors(true);
      return;
    }

    const result = await dispatch(applyDryRunShiftPlan());
    if (applyDryRunShiftPlan.rejected.match(result)) {
      dispatch(showError({
        title: 'Failed to Apply Changes',
        message: (result.payload as string) || 'An unexpected error occurred. Please try again.',
      }));
    }
  }, [dispatch, outputAdjustments]);

  // Handle generate plan on confirmation page
  const handleGenerate = useCallback(async () => {
    if (!planId) return;
    const result = await dispatch(generatePlanThunk({ planId, pageId: RCC_SHIFT_PLAN_CHECKOUT_TAB }));
    if (generatePlanThunk.fulfilled.match(result)) {
      navigate(ROUTES.SHIFT_PLAN_REVIEW);
    } else if (generatePlanThunk.rejected.match(result)) {
      dispatch(showError({
        title: 'Failed to Generate Plan',
        message: (result.payload as string) || 'An unexpected error occurred. Please try again.',
      }));
    }
  }, [dispatch, navigate, planId]);

  // Handle go back from confirmation page
  const handleGoBack = useCallback(() => {
    dispatch(dismissConfirmation());
  }, [dispatch]);

  // Compute displayed headcount for a path after adjustments (clamped to maxStaffing)
  const getDisplayedHeadcount = useCallback((entry: DryRunProcessPathEntry) => {
    if (isPathExcluded(entry.functionName)) return 0;
    const pct = getOverridePercentage(entry.functionName);
    const adjusted = Math.round(entry.headcount * pct / 100);
    if (entry.maxStaffing && adjusted > entry.maxStaffing) {
      return entry.maxStaffing;
    }
    return adjusted;
  }, [isPathExcluded, getOverridePercentage]);

  // Compute delta from baseline
  const getDelta = useCallback((entry: DryRunProcessPathEntry) => {
    const displayed = getDisplayedHeadcount(entry);
    return displayed - entry.headcount;
  }, [getDisplayedHeadcount]);

  // Compute adjusted hours for a path (same percentage as slider)
  const getDisplayedHours = useCallback((entry: DryRunProcessPathEntry) => {
    if (isPathExcluded(entry.functionName)) return 0;
    if (!entry.hours) return 0;
    const pct = getOverridePercentage(entry.functionName);
    return parseFloat((entry.hours * pct / 100).toFixed(2));
  }, [isPathExcluded, getOverridePercentage]);

  // Loading state
  if (dryRunLoading) {
    return (
      <Column alignmentHorizontal="center" spacing="300" spacingInset="500">
        <Loader size="large" />
        <Text type="b300" color="secondary">Loading process path allocations...</Text>
      </Column>
    );
  }

  // No data state
  if (!dryRunResponse) {
    return (
      <Column spacing="400" spacingInset="500">
        <Text type="b300" color="secondary">No allocation data available.</Text>
      </Column>
    );
  }

  // Show confirmation page when apply is successful
  if (showConfirmation) {
    return (
      <ConfirmPlanGeneration
        onGenerate={handleGenerate}
        onGoBack={handleGoBack}
        loading={generatePlanLoading}
      />
    );
  }

  return (
    <Column spacing="400" spacingInset="300" className="checkout-page">
      <Heading level={2} type="h400">Process Path HC Allocation &amp; Tuning</Heading>
      <Text type="b200" color="secondary">
        Adjust tuning percentage per process path using the slider. Exclude paths not being run this shift.
      </Text>

      <SummaryStatsRow
        availableHeadcount={dryRunResponse.availableHeadcount}
        tunedHeadcount={stats.tunedHeadcount}
        totalHours={stats.totalHours}
        directHours={stats.directHours}
        indirectHours={stats.indirectHours}
        includedPathCount={stats.includedPathCount}
        excludedPathCount={stats.excludedPathCount}
      />

      <Box overflowX="auto">
        <DataTable showRowDividers width="100%">
          <DataTableHead backgroundColor="secondary">
            <DataTableRow>
              <DataTableHeader width={50}>Include</DataTableHeader>
              <DataTableHeader>Process Path</DataTableHeader>
              <DataTableHeader>Type</DataTableHeader>
              <DataTableHeader>Original HC</DataTableHeader>
              <DataTableHeader>Max Staffing</DataTableHeader>
              <DataTableHeader width={180}>Tuning %</DataTableHeader>
              <DataTableHeader>
                <Row spacing="200" alignmentVertical="center" widths="fit">
                  Tuned HC
                  <Tooltip title="Aggregated Headcount for the process path in this shift" position="top">
                    <Icon tokens={infoKnockoutTokens}>Info</Icon>
                  </Tooltip>
                </Row>
              </DataTableHeader>
              <DataTableHeader>
                <Row spacing="200" alignmentVertical="center" widths="fit">
                  Total Hours
                  <Tooltip title="Total Hours for the process path in this shift" position="top">
                    <Icon tokens={infoKnockoutTokens}>Info</Icon>
                  </Tooltip>
                </Row>
              </DataTableHeader>
              <DataTableHeader>Delta</DataTableHeader>
              <DataTableHeader>Override Reason</DataTableHeader>
              <DataTableHeader>Hrs / 1000 units</DataTableHeader>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {sortedProcessPaths.map((entry) => {
              const isDirect = entry.type === ProcessPathFunctionType.DIRECT;
              const excluded = isPathExcluded(entry.functionName);
              const sliderValue = getSliderValue(entry.functionName);
              const adjustedHC = getDisplayedHeadcount(entry);
              const delta = getDelta(entry);

              // Calculate max slider value from maxStaffing constraint
              const maxSliderValue = entry.maxStaffing && entry.headcount > 0
                ? Math.round((entry.maxStaffing / entry.headcount) * 100)
                : 200;
              const isCappedAtMax = !excluded && adjustedHC >= (entry.maxStaffing ?? Infinity);

              const deltaClass = (() => {
                if (delta === 0) return 'delta--neutral';
                const absDelta = Math.abs(delta);
                const pctChange = entry.headcount > 0 ? (absDelta / entry.headcount) * 100 : 0;
                if (absDelta <= 1 || pctChange <= 5) return 'delta--warning';
                return 'delta--critical';
              })();

              return (
                <DataTableRow key={entry.functionName} highlightOnHover>
                  <DataTableCell>
                    <Checkbox
                      checked={!excluded}
                      onChange={() => handleExcludeToggle(entry)}
                      disabled={isDirect}
                    />
                  </DataTableCell>
                  <DataTableCell>
                    <Text
                      type="b300"
                      color={excluded ? 'secondary' : undefined}
                    >
                      <span className={excluded ? 'process-path-name--excluded' : ''}>
                        {entry.functionName}
                      </span>
                    </Text>
                  </DataTableCell>
                  <DataTableCell>
                    <Text type="b200" color={excluded ? 'secondary' : undefined}>
                      {entry.type === ProcessPathFunctionType.DIRECT ? 'Direct' : 'Indirect'}
                    </Text>
                  </DataTableCell>
                  <DataTableCell>
                    <Text type="b300" color={excluded ? 'secondary' : undefined}>
                      {entry.headcount}
                    </Text>
                  </DataTableCell>
                  <DataTableCell>
                    <Text type="b200" color="secondary">
                      {entry.maxStaffing ?? '—'}
                    </Text>
                  </DataTableCell>
                  <DataTableCell>
                    <TuningSlider
                      value={sliderValue}
                      disabled={excluded}
                      maxSliderValue={maxSliderValue}
                      onChange={(val) => handleSliderChange(entry, val)}
                    />
                  </DataTableCell>
                  <DataTableCell>
                    {excluded ? (
                      <Text type="b300" color="secondary">—</Text>
                    ) : (
                      <Column spacing="100">
                        <Text type="b300">{adjustedHC}</Text>
                        {isCappedAtMax && (
                          <Text type="b100" color="error">Capped at max staffing ({entry.maxStaffing})</Text>
                        )}
                      </Column>
                    )}
                  </DataTableCell>
                  <DataTableCell>
                    {excluded ? (
                      <Text type="b300" color="secondary">—</Text>
                    ) : (
                      <Text type="b300">{getDisplayedHours(entry)}</Text>
                    )}
                  </DataTableCell>
                  <DataTableCell>
                    {excluded ? (
                      <Text type="b300" color="secondary">—</Text>
                    ) : (
                      <Text type="b300" className={deltaClass}>
                        {delta >= 0 ? '+' : ''}{delta}
                      </Text>
                    )}
                  </DataTableCell>
                  <DataTableCell>
                    {(excluded || isPathModified(entry.functionName)) ? (
                      <Column spacing="200">
                        <Select
                          value={getDropdownValue(entry.functionName)}
                          onChange={(value) => handleReasonChange(entry, value as string)}
                          placeholder="Select reason"
                          size="small"
                          width={200}
                          error={showReasonErrors && !getSelectedReason(entry.functionName)}
                          errorMessage={showReasonErrors && !getSelectedReason(entry.functionName) ? 'Override reason is required' : undefined}
                        >
                          {OVERRIDE_REASON_OPTIONS.map((reason) => (
                            <SelectOption key={reason} value={reason} label={reason} />
                          ))}
                        </Select>
                        {isFreeTextReason(entry.functionName) && (
                          <Input
                            value={otherReasonText[entry.functionName] ?? (FREE_TEXT_REASONS.includes(getSelectedReason(entry.functionName) as any) ? '' : getSelectedReason(entry.functionName))}
                            onChange={(val: string) => setOtherReasonText((prev) => ({ ...prev, [entry.functionName]: val }))}
                            onBlur={() => handleOtherReasonCommit(entry, otherReasonText[entry.functionName] || '')}
                            placeholder="Enter reason"
                            size="small"
                            width={200}
                            error={showReasonErrors && FREE_TEXT_REASONS.includes(getSelectedReason(entry.functionName) as any)}
                            errorMessage={showReasonErrors && FREE_TEXT_REASONS.includes(getSelectedReason(entry.functionName) as any) ? 'Please enter a reason' : undefined}
                          />
                        )}
                      </Column>
                    ) : (
                      <Text type="b300" color="secondary">—</Text>
                    )}
                  </DataTableCell>
                  <DataTableCell>
                    {excluded ? (
                      <Text type="b300" color="secondary">—</Text>
                    ) : (
                      <Text type="b300">
                        {entry.hoursPer1000Units != null ? entry.hoursPer1000Units.toFixed(2) : '—'}
                      </Text>
                    )}
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </DataTableBody>
        </DataTable>
      </Box>

      <Row spacing="300" alignmentVertical="center">
        <Button type="primary" onClick={handleApplyChanges} disabled={appliedDryRunLoading}>
          {appliedDryRunLoading ? 'Applying...' : 'Apply Changes'}
        </Button>
        <Button type="tertiary" onClick={handleClearOverrides}>
          Reset All
        </Button>
      </Row>
    </Column>
  );
};

export default Checkout;

import './index.scss';